
/***
 * a library of short js utilities.
 ***/

import parseURL from "#src/js/urls.mts"
import { get, set } from "lodash"
import { defaultsDeep } from "lodash/fp"
import { computed, reactive, ref, Ref, unref, watch } from "vue"
import { useRoute } from "vue-router"

export function assertIsNotNull<T>(value: T | null | undefined): asserts value is Exclude<T, null | undefined> {
  if (value === null || value === undefined) {
    throw new Error("Not Null assertion failed")
  }
}

export function getCookie (name: string) {
  let cookieValue: string | null = null
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
        break
      }
    }
  }
  return cookieValue
}

export const csrfTokenHeader = 'X-CSRFToken'
export const csrfTokenCookie = 'csrftoken'
export const csrfTokenFormField = 'csrfmiddlewaretoken'

export function getCSRFToken() {
  // cookie source. This should always be used if available according to https://docs.djangoproject.com/en/4.2/howto/csrf/
  return getCookie('csrftoken') as string
}

export const csrfFormDataListener = ((event: FormDataEvent) => {
  const { formData } = event
  const token = getCSRFToken()

  formData.set(csrfTokenFormField, token)
}) as EventListener

/**
 * Calculates luminoscity to use with picking foreground / background colors
 */
const luminoscityWeights = [ .3, .59, .11 ] as const

export function luminoscity(color: string) {
  const _color = color.startsWith('#') ? color.substring(1) : color

  const length = Math.floor(_color.length / 3)
  const chunks = Array(3).fill(0).map((__, index) => _color.substring(index * length, (index+1)*length))

  const dec = chunks.map(v => parseInt(v, 16))
  const luminoscity = dec
    .map((v, i) => v * luminoscityWeights[i])
    .reduce((T, v) => T+v)

  return luminoscity
}

export function getHighContrastColorClass(bg: string) {
  const tag = bg.split('-').reverse()[0]
  const colorString = getComputedStyle(document.body).getPropertyValue(`--bs-${tag}`)
  const lum = luminoscity(colorString)

  const color = lum < 128
    ? 'text-white'
    : 'text-black'

  return color
}

/**
 * generates lodash paths for each leaf of the given object.
 */
export function * generatePaths(obj: any, prefix?: string[]) {
  const _prefix = prefix ?? []
  for( const [key, value] of Object.entries(obj ?? {}) ) {
    _prefix.push(key)
    if ((typeof value) === 'object' && value !== null) {
      yield* generatePaths(value, _prefix)
    } else {
      yield _prefix.join('.')
    }
    _prefix.pop()
  }
}

/**
 * Converts the incoming object to paths, and then assigns these paths to the core object.
 * Garantuees that reactivity is preserved
 */
export function reactiveAssign<Value extends object>(object: Value, incoming: Value): Value {
  const paths = [...generatePaths(incoming)]
  for (const path of paths) {
    const value = get(incoming, path)
    set(object, path, value)
  }
  return object
}

export type DelayedTransitionOptions = {
  // delay in millieseconds. defaults to 500
  delay?: number
  // delay leading edge. Defaults to false
  leading?: boolean
  // delay trailing edge. Defaults to true
  trailing?: boolean
}
export type DelayedTransitionState<V extends any> = {
  promise: Promise<V>
  pending: boolean
}
async function _applyDelayedValue<V extends any>(delayedValue: Ref<V>, value: V, delay: number | null, pending: Promise<V>) {
  await pending
  if (delay !== null) {
    await new Promise<void>((resolve) => setTimeout(() => (resolve()), delay))
  }
  delayedValue.value = value
  return value
}
export function useDelayedTransitionValue<V extends any>(value: Ref<V>, options?: DelayedTransitionOptions) {
  const delayedValue = ref(unref(value)) as Ref<V>
  const state: DelayedTransitionState<V> = reactive({
    promise: Promise.resolve(unref(value)),
    pending: false,
  })

  watch(value, () => {
    const { delay = 500, leading = false, trailing = true } = (options ?? {})
    const { promise } = state

    const _value = unref(value)
    const isLeading = Boolean(_value)

    const shouldDelay = (leading && isLeading) || (trailing && !isLeading)
    const _delay = shouldDelay ? delay : null

    state.promise = _applyDelayedValue(delayedValue, _value, _delay, promise)
  })

  return { delayedValue, value, state }
}

export function sleep(delay: number) {
  return new Promise<void>(resolve => {
    setTimeout(() => resolve(), delay)
  })
}

const moduleEnumData: Record<string, WeakRef<any>> = {}
export function useModuleEnums(module: string) {
  if (module in moduleEnumData) {
    const _ref = moduleEnumData[module]
    const data = _ref.deref()
    if (data !== undefined) {
      return { enumData: data }
    }
  }

  const data = reactive({})
  moduleEnumData[module] = new WeakRef(data)

  const route = useRoute()
  const lang = computed(() => route.meta.lang)

  let abortController: AbortController | null = null

  watch(lang, async () => {
    abortController?.abort()
    const _abortController = new AbortController()
    abortController = _abortController

    const url = parseURL("DjangoData", { module })

    const response = await fetch(url)
    const jsonData = await response.json()

    Object.assign(data, jsonData)
    console.debug("Update enum data", data)
  }, { immediate: true })

  return { enumData: data }
}

export function useEnumTranslation(data: object, varName: string, module: string) {
  const { enumData } = useModuleEnums(module)
  const _defaults = defaultsDeep(data)

  const translated = computed(() => _defaults(enumData[varName]?.data ?? {}))

  console.debug("translated enum", data, translated, _defaults({}))

  return { translated }
}
